import { useEffect, useRef, useState } from "react";
import { bool, string } from "prop-types";
import cx from "classnames";
import debounce from "lodash.debounce";
import FlagPlainIcon from "../Icon/FlagPlainIcon.jsx";
import Popup from "../../../../Common/js/components/Popup/Popup.jsx";
import { useAnnotationContext } from "./AnnotationContext.jsx";
import { highlightTypes } from "./annotationCommonPropTypes.js";
import { useFauxSelection } from "./useFauxSelection.jsx";
import useBoundingClientRect from "../../common/useBoundingClientRect.js";

const propTypes = {
  width: string.isRequired,
  height: string.isRequired,
  highlightType: highlightTypes,
  isAnnotated: bool.isRequired,
};

const PlainAnnotationMarker = (props) => {
  const { width, height, highlightType, isAnnotated } = props;
  // Highlight Type is not used currently; would be used if we need to expand
  // the functionality of the Plain Flag marker to allow overlays in future.

  const {
    data: {
      content: { createNewAnnotationText, viewAddAnnotationText },
    },
    marker: { markerPositionTop, markerPositionRight, markerNoColor },
    state: { isModalOpen, isSelectionActive },
    actions: {
      activateSelection,
      deactivateSelection,
      openModalWithSelection,
      closeModal,
    },
  } = useAnnotationContext();

  // Get a ref to the marker, so we can affect the non-React DOM
  const annotationMarkerRef = useRef(null);

  // get the button width so we can place the popup
  const [{ width: buttonWidth }, buttonRef] = useBoundingClientRect();

  // Create a variable to capture the conditions for activating/deactivating listening
  const listeningForSelection =
    !isModalOpen && !isSelectionActive && !isAnnotated;

  // Create a variable to capture the conditions for the Create New Button active
  const isCreateButtonActive =
    !isModalOpen && isSelectionActive && !isAnnotated;

  // Component-level states for the tooltip, only triggered by this component
  const [tooltipHidden, setTooltipHidden] = useState(true);

  // Dev note: Many of the useEffects here are to manipulate some non-React
  // DOM elements which are not inside the component.

  // Adds a class, or adds a "Create New Annotation" button
  // based on whether annotations are present or not
  useEffect(() => {
    // If there are existing annotations, add a "has-annotations" class
    // to the annotation-wrapper div
    if (isAnnotated) {
      // Select the annotation wrapper div in the DOM from our ref point
      const hasAnnotationsClassDiv = annotationMarkerRef.current.closest(
        ".js-annotation-wrapper",
      );

      hasAnnotationsClassDiv.classList.add(
        "annotation-wrapper--has-annotation",
      );

      return () => {
        hasAnnotationsClassDiv.classList.remove(
          "annotation-wrapper--has-annotation",
        );
      };
    }
    // If not annotated, insert a "Create New Annotation" button into the DOM.
    // This would only happen once on load, as we will never go from
    // "has annotations" to "doesn't have annotations".
    else {
      // Select the annotatable content from our marker ref
      const buttonTargetDiv = annotationMarkerRef.current.closest(
        ".annotation-wrapper__shrinkwrap",
      ).firstElementChild;

      // Create the "Create New Annotation" button
      const createAnnotationButton = document.createElement("button");
      createAnnotationButton.innerText = createNewAnnotationText;
      createAnnotationButton.classList.add(
        "annotation-create-tooltip",
        "popup",
        "popup--auto-width",
        "popup--triangle-bottom",
      );
      const createAnnotationButtonTriangle = document.createElement("span");
      createAnnotationButtonTriangle.classList.add("popup__triangle");
      createAnnotationButton.appendChild(createAnnotationButtonTriangle);

      // When the Create New Annotation button is clicked,
      // it fires a click event on the hidden React Annotation Marker button
      const handleCreateClick = () => {
        annotationMarkerRef.current.firstElementChild.click();
      };

      // Bind the click event to the button
      createAnnotationButton.addEventListener("click", handleCreateClick);

      // Insert the button into the DOM
      buttonTargetDiv.parentNode.insertBefore(
        createAnnotationButton,
        buttonTargetDiv,
      );

      return () => {
        createAnnotationButton.removeEventListener("click", handleCreateClick);
        createAnnotationButton.parentNode.removeChild(createAnnotationButton);
      };
    }
  }, [createNewAnnotationText, isAnnotated]);

  // Sets up listening within the content to be annotated,
  // for a selection event to trigger our faux selection
  useEffect(() => {
    if (listeningForSelection) {
      // Get our selection div from the marker ref
      const selectionDiv = annotationMarkerRef.current
        .closest(".annotation-wrapper__shrinkwrap")
        .querySelector(".annotation-create-tooltip").nextSibling;

      // When the selecting has settled (selectionEnd), activate the selection
      const handleSelectionChanged = debounce(() => {
        document.removeEventListener("selectionchange", handleSelectionChanged);
        activateSelection();
      }, 500);

      // Once selection starts, bind our custom debounced event to selectionchange
      const handleSelectStart = () => {
        document.addEventListener("selectionchange", handleSelectionChanged);
      };

      // Initialize everything when the user starts selecting on the div
      selectionDiv.addEventListener("selectstart", handleSelectStart);

      return () => {
        document.removeEventListener("selectionchange", handleSelectionChanged);
        selectionDiv.removeEventListener("selectstart", handleSelectStart);
      };
    }
  }, [activateSelection, listeningForSelection]);

  // Sets up listening for a click outside the marker, when the
  // "Create New Annotation" button is active
  useEffect(() => {
    if (isCreateButtonActive) {
      // A click on the Create Button triggers a click on the marker,
      // so we actually have to test for the marker as the click target!
      const markerButton = annotationMarkerRef.current;

      const outsideClickListener = (event) => {
        if (!markerButton.contains(event.target)) {
          deactivateSelection();
        }
      };

      document.addEventListener("click", outsideClickListener);

      return () => {
        document.removeEventListener("click", outsideClickListener);
      };
    }
  }, [deactivateSelection, isCreateButtonActive]);

  // If the modal is open, add a "modal-open" class to the
  // annotation wrapper div, to hide the "Create New Annotation" button
  useEffect(() => {
    if (isModalOpen) {
      // Select the target div in the DOM from our ref point
      const hasAnnotationsClassDiv = annotationMarkerRef.current.closest(
        ".js-annotation-wrapper",
      );
      hasAnnotationsClassDiv.classList.add("annotation-wrapper--modal-open");

      return () => {
        hasAnnotationsClassDiv.classList.remove(
          "annotation-wrapper--modal-open",
        );
      };
    }
  }, [annotationMarkerRef, isModalOpen]);

  // Create a faux selection state for the content to be annotated
  useFauxSelection(isSelectionActive, annotationMarkerRef);

  // Event handlers
  const handleMouseEnter = (event) => {
    setTooltipHidden(false);
    activateSelection();
  };
  const handleMouseLeave = (event) => {
    setTooltipHidden(true);
    if (!isModalOpen) {
      deactivateSelection();
    }
  };
  const handleClick = (event) => {
    if (!isModalOpen) {
      openModalWithSelection();
    } else {
      closeModal();
    }
  };

  // Creating some boolean variables for prop assignments below
  const hasMarkerPositionTop = markerPositionTop ? true : false;
  const hasMarkerPositionRight = markerPositionRight ? true : false;
  const hasNoColor = markerNoColor ? true : false;

  return (
    <div
      ref={annotationMarkerRef}
      className={cx("annotation-marker", {
        "annotation-marker--no-color": hasNoColor,
        "has-annotation": isAnnotated,
      })}
    >
      <button
        type="button"
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
        onClick={handleClick}
        style={{
          top: hasMarkerPositionTop ? markerPositionTop + "px" : "none",
          right: hasMarkerPositionRight ? markerPositionRight + "px" : "none",
        }}
        ref={buttonRef}
      >
        <FlagPlainIcon
          title={viewAddAnnotationText}
          width={width}
          height={height}
        />

        {buttonWidth && (
          <Popup
            isHidden={tooltipHidden}
            left={buttonWidth / 2}
            top={0}
            offset={buttonWidth / 2}
            triangleAlignment={0}
            triangleSide="left"
            triangleSize={0} // we're hiding the triangle via css
            flipToContain="viewport"
            nudgeToContain="viewport"
            width={158}
            className="annotation-marker__popup"
          >
            <span>{viewAddAnnotationText}</span>
          </Popup>
        )}
      </button>
    </div>
  );
};

PlainAnnotationMarker.propTypes = propTypes;
export default PlainAnnotationMarker;
